import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { useSelector, useStore } from 'react-redux';
import { portalApi, fetchBackgroundData } from '../redux/api/portalApi';
import { formActivity } from './formActivity';
import { resourceForUrl } from './resources';
import { TBD_FINDER_PAUSED } from '../config/tbdFinder';
import { useRealtimeResource, useRealtimeState } from './RealtimeProvider';

// Reject unknown envelopes rather than treating an error response as an empty list.
export function readListRows(payload) {
  const rows = Array.isArray(payload) ? payload : payload?.data;
  if (!Array.isArray(rows) || rows.some(row => !row || typeof row !== 'object' || Array.isArray(row))) throw new Error('INVALID_LIST_SNAPSHOT');
  return rows;
}

const editing = (blockDialogs = true) => Boolean((blockDialogs && document.querySelector('.modal.show, [role="dialog"]')) ||
  document.activeElement?.matches('input:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled]), [contenteditable="true"]'));
const busy = state => Object.values(state[portalApi.reducerPath]?.queries || {}).some(x => x.status === 'pending') ||
  Object.values(state[portalApi.reducerPath]?.mutations || {}).some(x => x.status === 'pending');
const noopSubscribe = () => () => {};
const inactiveSnapshot = () => false;

export default function useGuardedRealtimeList({ apiKey, apply, enabled = true, params, blockDialogs = true, ownerSuffix, blocked = false, additionalResources = [], decode = readListRows, load, scopeKey }) {
  enabled = enabled && !(TBD_FINDER_PAUSED && apiKey === 'tbdValues');
  const instanceId = useId();
  const store = useStore();
  const { mode } = useRealtimeState();
  const url = useSelector(state => state.apiUrls?.urls?.[apiKey]);
  const base = useSelector(state => state.apiUrls?.urls?.generalProjectInfoEnhanced);
  const user = useSelector(state => state.user?.userDetails);
  let resource;
  try { resource = resourceForUrl(url, new URL('.', base).href); } catch { /* API not configured. */ }
  const active = Boolean(enabled && resource && ['domain', 'session'].includes(mode));
  const pending = useSelector(state => active && busy(state));
  useSyncExternalStore(active ? formActivity.subscribe : noopSubscribe, active ? formActivity.snapshot : inactiveSnapshot);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!active) {
      setFocused(false);
      return undefined;
    }
    const check = () => setFocused(editing(blockDialogs));
    document.addEventListener('focusin', check);
    document.addEventListener('focusout', check);
    const observer = new MutationObserver(check);
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    check();
    return () => { observer.disconnect(); document.removeEventListener('focusin', check); document.removeEventListener('focusout', check); };
  }, [active, blockDialogs]);
  const current = useRef({});
  const scope = JSON.stringify([params || {}, scopeKey]);
  current.current = { enabled, url, user, scope, blocked };
  useRealtimeResource({
    enabled: active,
    ownerKey: `guarded-list:${user?.localAccountId || user?.username}:${url}:${scope}:${ownerSuffix ?? instanceId}`,
    resources: resource ? [resource, ...additionalResources] : [],
    blocked: blocked || pending || focused || formActivity.blocked(),
    refresh: async ({ signal }) => {
      const valid = () => !signal.aborted && current.current.enabled && !current.current.blocked && current.current.url === url && current.current.user === user && current.current.scope === scope &&
        store.getState().apiUrls?.urls?.[apiKey] === url && store.getState().user?.userDetails === user &&
        !busy(store.getState()) && !editing(blockDialogs) && !formActivity.blocked();
      if (!valid()) return false;
      // A foreground request/save that starts AND finishes during this GET still
      // invalidates its snapshot. Do not rely only on the state at completion.
      let superseded = false;
      const release = store.subscribe(() => { if (!valid()) superseded = true; });
      const releaseDraft = formActivity.subscribe(() => { if (!valid()) superseded = true; });
      const observeEdit = () => { if (!valid()) superseded = true; };
      document.addEventListener('focusin', observeEdit);
      const observer = new MutationObserver(observeEdit);
      observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      try {
        const data = load ? await load({ signal }) : await store.dispatch(fetchBackgroundData({ url, signal, params }));
        let rows;
        try { rows = decode(data); } catch { return false; }
        if (superseded || !valid()) return false;
        apply(rows);
        return true;
      } finally { release(); releaseDraft(); observer.disconnect(); document.removeEventListener('focusin', observeEdit); }
    },
  });
}
